import React, { useEffect, useState } from "react";
import axiosInstance from "../../../axiosInstance";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import { Search, X } from "lucide-react"; // Import Trash2 icon
import "./PaymentSetupContent.scss";
import { toast } from "react-toastify";
import ColoredCheckbox from "../../../components/ColoredCheckbox/ColoredCheckbox";
import IconButton from "../../../components/IconButton/IconButton";
import ToggleButton from "../../../components/ToggleButton/ToggleButton";
import WarningPopup from "../../../components/WarningPopup/WarningPopup";

function PaymentSetupContent({ payrollId }) {
  const [payrollData, setPayrollData] = useState([]);
  const [paymentSetups, setPaymentSetups] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // setup awaiting delete confirmation
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await axiosInstance.get(
          `${process.env.REACT_APP_API_BASE_URL}/payslip/image-previews`,
        );
        setTemplates(res.data.data);
      } catch (err) {
        console.error("Failed to fetch templates", err);
      }
    };

    if (isEditing) {
      fetchTemplates();
    }
  }, [isEditing]);

  useEffect(() => {
    if (!payrollId) {
      setError("Payroll ID is required");
      return;
    }

    const fetchPayrollData = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_BASE_URL}/payroll/details?payrollId=${payrollId}`,
        );

        if (response.status === 200) {
          const data = response.data.data;
          // Normalize all values to internal format (uppercase)
          const normalizedPaymentSetups = (data.paymentSetups || []).map(
            (setup) => ({
              ...setup,
              status: convertApiToInternal(setup.status) || "ENABLED",
              paymentCalculationBasedOn:
                convertApiToInternal(setup.paymentCalculationBasedOn) ||
                "GROSS_SALARY",
              // Ensure other fields are also properly formatted
              salaryType: setup.salaryType?.toUpperCase() || "EARNING",
              contributionType:
                setup.contributionType?.toUpperCase() || "EMPLOYEE",
              valueType: setup.valueType?.toUpperCase() || "PERCENTAGE",
              taxType: setup.taxType?.toUpperCase() || "NIL",
              paymentSetupBasedOnNames: setup.paymentSetupBasedOnNames || [],
            }),
          );

          setPayrollData(data);
          setPaymentSetups(normalizedPaymentSetups);
          setSelectedTemplateId(data.payslipTemplateId);
        }
      } catch (error) {
        console.error("Error fetching payroll data:", error);
        setError("An error occurred while fetching payroll data.");
      } finally {
        setLoading(false);
      }
    };

    fetchPayrollData();
  }, [payrollId]);

  const handleRemainingEarningsChange = (selectedIndex) => {
    const updated = paymentSetups.map((item, index) => ({
      ...item,
      remainingEarnings: index === selectedIndex,
    }));
    setPaymentSetups(updated);
  };

  const handleChange = (index, field, value) => {
    const updated = paymentSetups.map((item, i) => {
      if (i === index) {
        const updatedItem = { ...item, [field]: value };
        if (field === "paymentCalculationBasedOn" && value === "GROSS_SALARY") {
          updatedItem.paymentSetupBasedOnNames = [];
        }
        // Reset value and limitAmount when tax type is IT or PT
        if (field === "taxType" && (value === "IT" || value === "PT")) {
          updatedItem.value = 0;
          updatedItem.limitAmount = 0;
        }
        return updatedItem;
      }
      return item;
    });
    setPaymentSetups(updated);
  };

  const handleToggleBasedOn = (index, name) => {
    setPaymentSetups((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        const current = item.paymentSetupBasedOnNames || [];
        return {
          ...item,
          paymentSetupBasedOnNames: current.includes(name)
            ? current.filter((n) => n !== name)
            : [...current, name],
        };
      }),
    );
  };

  const convertApiToInternal = (apiValue) => {
    const conversionMap = {
      "Gross Salary": "GROSS_SALARY",
      "Payment Setup": "PAYMENT_SETUP",
      Enabled: "ENABLED",
      Disabled: "DISABLED",
    };
    return conversionMap[apiValue] || apiValue;
  };

  const convertInternalToApi = (internalValue) => {
    const conversionMap = {
      GROSS_SALARY: "GROSS_SALARY",
      PAYMENT_SETUP: "PAYMENT_SETUP",
      ENABLED: "ENABLED",
      DISABLED: "DISABLED",
    };
    return conversionMap[internalValue] || internalValue;
  };

  const handleStatusToggle = async (index, setup) => {
    // If it's a new setup (no paymentSetupId), just update locally
    if (!setup.paymentSetupId) {
      const updated = paymentSetups.map((item, i) => {
        if (i === index) {
          return {
            ...item,
            status: item.status === "ENABLED" ? "DISABLED" : "ENABLED",
          };
        }
        return item;
      });
      setPaymentSetups(updated);
      return;
    }

    try {
      const isCurrentlyEnabled = setup.status === "ENABLED";
      const endpoint = isCurrentlyEnabled ? "disable" : "enable";

      await axiosInstance.put(
        `${process.env.REACT_APP_API_BASE_URL}/payment-setup/${setup.paymentSetupId}/status/${endpoint}`,
      );

      // Update local state
      const updated = paymentSetups.map((item, i) => {
        if (i === index) {
          return {
            ...item,
            status: isCurrentlyEnabled ? "DISABLED" : "ENABLED",
          };
        }
        return item;
      });
      setPaymentSetups(updated);

      toast.success(
        `Payment setup ${
          isCurrentlyEnabled ? "disabled" : "enabled"
        } successfully!`,
      );
    } catch (error) {
      console.error("Error updating payment setup status:", error);
      toast.error("Failed to update payment setup status.");
    }
  };

  // Removes a setup from the screen and drops references to its name in other setups
  const removeSetupLocally = (setupToRemove) => {
    const isSame = (s) =>
      setupToRemove.paymentSetupId
        ? s.paymentSetupId === setupToRemove.paymentSetupId
        : s === setupToRemove;

    const remaining = paymentSetups.filter((s) => !isSame(s));
    const nameStillExists = remaining.some(
      (s) => s.salaryName === setupToRemove.salaryName,
    );
    const dropName = (names) =>
      (names || []).filter(
        (name) => nameStillExists || name !== setupToRemove.salaryName,
      );
    const dependents = nameStillExists
      ? []
      : remaining.filter((s) =>
          (s.paymentSetupBasedOnNames || []).includes(setupToRemove.salaryName),
        );

    setPaymentSetups((prev) =>
      prev
        .filter((s) => !isSame(s))
        .map((s) => ({
          ...s,
          paymentSetupBasedOnNames: dropName(s.paymentSetupBasedOnNames),
        })),
    );

    // keep the "saved" copy in sync so Cancel doesn't bring a deleted setup back
    if (setupToRemove.paymentSetupId) {
      setPayrollData((prev) => ({
        ...prev,
        paymentSetups: (prev.paymentSetups || [])
          .filter((s) => s.paymentSetupId !== setupToRemove.paymentSetupId)
          .map((s) => ({
            ...s,
            paymentSetupBasedOnNames: dropName(s.paymentSetupBasedOnNames),
          })),
      }));
    }

    if (dependents.length > 0) {
      toast.warn(
        `${setupToRemove.salaryName} was removed from: ${dependents
          .map((d) => d.salaryName || "new setup")
          .join(", ")}. Review their Predefined Setup and save.`,
      );
    }
  };

  const handleRemove = (indexToRemove) => {
    const setupToRemove = paymentSetups[indexToRemove];

    if (setupToRemove.paymentSetupId) {
      // Saved setup: ask for confirmation, the delete API is called on accept
      setDeleteTarget(setupToRemove);
    } else {
      // Not saved yet: nothing to delete on the server
      removeSetupLocally(setupToRemove);
      toast.info("Payment setup removed.");
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget || isDeleting) return;
    const setup = deleteTarget;

    try {
      setIsDeleting(true);
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_BASE_URL}/payment-setup/${setup.paymentSetupId}/delete`,
      );
      removeSetupLocally(setup);
      toast.success(`"${setup.salaryName}" deleted successfully.`);
    } catch (error) {
      console.error("Error deleting payment setup:", error);
      toast.error(
        error.response?.data?.message || "Failed to delete payment setup.",
      );
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleCancelDelete = () => {
    if (!isDeleting) setDeleteTarget(null);
  };

  const handleAddPaymentSetup = () => {
    const newSetup = {
      paymentSetupId: null, // null for new setups
      salaryType: "EARNING",
      salaryName: "",
      contributionType: "EMPLOYEE",
      value: 0,
      limitAmount: 0,
      valueType: "PERCENTAGE",
      status: "ENABLED",
      paymentCalculationBasedOn: "GROSS_SALARY",
      paymentSetupBasedOnNames: [],
      taxType: "NIL",
      remainingEarnings: false,
      fixedSetup: false,
    };

    setPaymentSetups([...paymentSetups, newSetup]);
    toast.info("New payment setup added. Configure and save to confirm.");
  };

  const handleSave = async () => {
    try {
      // Separate new setups from existing ones
      const newSetups = paymentSetups.filter((setup) => !setup.paymentSetupId);
      const existingSetups = paymentSetups.filter(
        (setup) => setup.paymentSetupId,
      );

      // Validate that new setups have required fields
      const invalidNewSetups = newSetups.filter(
        (setup) => !setup.salaryName || setup.salaryName.trim() === "",
      );

      if (invalidNewSetups.length > 0) {
        toast.error("Please provide salary names for all new payment setups.");
        return;
      }

      const missingBase = paymentSetups.filter(
        (setup) =>
          setup.paymentCalculationBasedOn === "PAYMENT_SETUP" &&
          !(setup.paymentSetupBasedOnNames || []).filter((name) =>
            paymentSetups.some((s) => s.salaryName === name),
          ).length,
      );
      if (missingBase.length > 0) {
        toast.error(
          `Select at least one Predefined Setup for: ${missingBase
            .map((s) => s.salaryName || "new setup")
            .join(", ")}`,
        );
        return;
      }

      // Only send based-on names that still exist in this payroll
      const currentNames = paymentSetups.map((s) => s.salaryName);
      const safeBasedOnNames = (setup) =>
        setup.paymentCalculationBasedOn === "PAYMENT_SETUP"
          ? [...new Set(setup.paymentSetupBasedOnNames || [])].filter(
              (name) =>
                name !== setup.salaryName && currentNames.includes(name),
            )
          : [];

      // Convert existing setups to API format
      const filteredPaymentSetups = existingSetups.map((setup) => ({
        paymentSetupId: setup.paymentSetupId,
        salaryType: setup.salaryType?.toUpperCase(), // Ensure uppercase
        salaryName: setup.salaryName,
        contributionType: setup.contributionType?.toUpperCase(), // Ensure uppercase
        value: Number(setup.value),
        limitAmount: Number(setup.limitAmount),
        valueType: setup.valueType?.toUpperCase(), // Ensure uppercase
        status: convertInternalToApi(setup.status), // ✅ force uppercase
        paymentCalculationBasedOn: convertInternalToApi(
          setup.paymentCalculationBasedOn,
        ),
        paymentSetupBasedOnNames: safeBasedOnNames(setup),
        taxType: setup.taxType?.toUpperCase(), // Ensure uppercase
        remainingEarnings: setup.remainingEarnings,
        fixedSetup: setup.fixedSetup,
      }));

      // Convert new setups to API format
      const newSetupsForPayload = newSetups.map((setup) => ({
        salaryType: setup.salaryType?.toUpperCase(), // Ensure uppercase
        salaryName: setup.salaryName,
        contributionType: setup.contributionType?.toUpperCase(), // Ensure uppercase
        value: Number(setup.value),
        limitAmount: Number(setup.limitAmount),
        valueType: setup.valueType?.toUpperCase(), // Ensure uppercase
        status: convertInternalToApi(setup.status), // Convert to API format
        paymentCalculationBasedOn: convertInternalToApi(
          setup.paymentCalculationBasedOn,
        ), // Convert to API format
        paymentSetupBasedOnNames: safeBasedOnNames(setup),
        taxType: setup.taxType?.toUpperCase(), // Ensure uppercase
        remainingEarnings: setup.remainingEarnings,
        fixedSetup: setup.fixedSetup,
      }));

      const payload = {
        payrollId: payrollData.payrollId,
        payrollName: payrollData.payrollName,
        payrollSetupType: payrollData.payrollSetupType,
        paymentSetups: [...filteredPaymentSetups, ...newSetupsForPayload],
        payslipTemplateId: selectedTemplateId,
      };

      console.log("update payroll payload:", JSON.stringify(payload, null, 2));

      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_BASE_URL}/payroll/update`,
        payload,
      );

      if (response.status === 200) {
        toast.success("Payment setups updated successfully!");
        setIsEditing(false);

        // Refresh the data to get the new paymentSetupIds for newly created setups
        const refreshResponse = await axiosInstance.get(
          `${process.env.REACT_APP_API_BASE_URL}/payroll/details?payrollId=${payrollId}`,
        );

        if (refreshResponse.status === 200) {
          const data = refreshResponse.data.data;
          // Normalize all values to internal format (uppercase)
          const normalizedPaymentSetups = (data.paymentSetups || []).map(
            (setup) => ({
              ...setup,
              status: convertApiToInternal(setup.status) || "ENABLED",
              paymentCalculationBasedOn:
                convertApiToInternal(setup.paymentCalculationBasedOn) ||
                "GROSS_SALARY",
              // Ensure other fields are also properly formatted
              salaryType: setup.salaryType?.toUpperCase() || "EARNING",
              contributionType:
                setup.contributionType?.toUpperCase() || "EMPLOYEE",
              valueType: setup.valueType?.toUpperCase() || "PERCENTAGE",
              taxType: setup.taxType?.toUpperCase() || "NIL",
              paymentSetupBasedOnNames: setup.paymentSetupBasedOnNames || [],
            }),
          );

          setPayrollData(data);
          setPaymentSetups(normalizedPaymentSetups);
        }
      }
    } catch (error) {
      console.error("Error updating data:", error);
      toast.error("Update failed.");
    }
  };

  const toggleEdit = () => {
    if (isEditing) {
      handleSave();
    } else {
      setIsEditing(true);
    }
  };

  const formatDisplayText = (text) => {
    if (!text) return "-";
    return text
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getDisplayOptions = (options) => {
    return options.map((option) => ({
      value: option,
      label: formatDisplayText(option),
    }));
  };

  const salaryTypeOptions = getDisplayOptions(["EARNING", "CONTRIBUTION"]);
  const contributionTypeOptions = getDisplayOptions(["EMPLOYEE", "EMPLOYER"]);

  // Helper function to check if value and limit amount should be disabled
  const isValueFieldDisabled = (taxType) => {
    return taxType === "IT" || taxType === "PT";
  };

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (loading) {
    return <LoadingScreen />;
  }

  const handleCancel = () => {
    // Normalize all values when canceling
    const normalizedPaymentSetups = (payrollData.paymentSetups || []).map(
      (setup) => ({
        ...setup,
        status: convertApiToInternal(setup.status) || "ENABLED",
        paymentCalculationBasedOn:
          convertApiToInternal(setup.paymentCalculationBasedOn) ||
          "GROSS_SALARY",
        // Ensure other fields are also properly formatted
        salaryType: setup.salaryType?.toUpperCase() || "EARNING",
        contributionType: setup.contributionType?.toUpperCase() || "EMPLOYEE",
        valueType: setup.valueType?.toUpperCase() || "PERCENTAGE",
        taxType: setup.taxType?.toUpperCase() || "NIL",
        paymentSetupBasedOnNames: setup.paymentSetupBasedOnNames || [],
      }),
    );

    setPaymentSetups(normalizedPaymentSetups);
    setSelectedTemplateId(payrollData.payslipTemplateId || null);
    setIsEditing(false);
  };

  return (
    <div className="payment-setup-container">
      <WarningPopup
        isOpen={!!deleteTarget}
        onClose={handleCancelDelete}
        onDecline={handleCancelDelete}
        onAccept={handleConfirmDelete}
        title="Delete Payment Setup"
        message={`Are you sure you want to delete "${
          deleteTarget?.salaryName || "this payment setup"
        }"? This will permanently remove it from this payroll and cannot be undone.`}
        acceptText={isDeleting ? "Deleting..." : "Delete"}
        declineText="Cancel"
      />
      <div className="header-container">
        <h2 className="page-title">
          Payment Setup for Payroll: {payrollData.payrollName || "Unknown"}
        </h2>
        <div className="action-buttons">
          {isEditing && (
            <>
              <ButtonWithIcon
                type="button"
                onClick={handleAddPaymentSetup}
                className="add-button"
                icon="ic:round-add"
                text="Add Payment Setup"
              />
              <ButtonWithIcon
                type="button"
                onClick={handleCancel}
                className="cancel-button"
                icon="ic:round-close"
                text="Cancel"
              />
            </>
          )}
          <ButtonWithIcon
            onClick={toggleEdit}
            className={isEditing ? "save-button" : "edit-button"}
            icon={isEditing ? "tdesign:send" : "tdesign:edit"}
            text={isEditing ? "Save Changes" : "Edit"}
          />
        </div>
      </div>

      {isEditing && (
        <div className="template-section">
          <h3 className="section-title">Payslip Template Selection</h3>
          <div className="template-grid">
            {templates.map((template) => (
              <div
                key={template.payslipTemplateId}
                className={`template-card ${
                  selectedTemplateId === template.payslipTemplateId
                    ? "selected"
                    : ""
                }`}
                onClick={() =>
                  setSelectedTemplateId(template.payslipTemplateId)
                }
              >
                <div className="template-image-container">
                  <img
                    src={`data:image/png;base64,${template.base64}`}
                    alt={template.fileName}
                    className="template-image"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEnlargedImage(template.base64);
                    }}
                    className="enlarge-btn"
                    aria-label="Enlarge Image"
                  >
                    <Search size={16} />
                  </button>
                </div>
                <p className="template-name">{template.fileName}</p>
              </div>
            ))}
          </div>

          {enlargedImage && (
            <div
              className="modal-overlay"
              onClick={() => setEnlargedImage(null)}
            >
              <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setEnlargedImage(null)}
                  className="close-btn"
                  aria-label="Close preview"
                >
                  <X size={24} />
                </button>
                <img
                  src={`data:image/png;base64,${enlargedImage}`}
                  alt="Enlarged template"
                  className="enlarged-image"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {paymentSetups.length > 0 ? (
        <div className="table-container">
          <table className="payment-table">
            <thead>
              <tr>
                {payrollData.payrollSetupType === "MANUAL" ? (
                  <>
                    <th>Salary Type</th>
                    <th>Salary Name</th>
                    <th>Contribution Type</th>
                    <th>Status</th>
                    {isEditing && <th>Actions</th>}
                  </>
                ) : (
                  <>
                    <th>Salary Type</th>
                    <th>Salary Name</th>
                    <th>Contribution Type</th>
                    <th>Value</th>
                    <th>Value Type</th>
                    <th>Limit Amount</th>
                    <th>Tax Type</th>
                    <th>Calculation Base</th>
                    <th>Predefined Setup</th>
                    <th>Remaining Earnings</th>
                    <th>Status</th>
                    {isEditing && <th>Actions</th>}
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {paymentSetups.map((setup, index) => (
                <tr key={setup.paymentSetupId || index}>
                  {payrollData.payrollSetupType === "MANUAL" ? (
                    <>
                      <td>
                        {isEditing ? (
                          <select
                            className="form-select"
                            value={setup.salaryType || ""}
                            onChange={(e) =>
                              handleChange(index, "salaryType", e.target.value)
                            }
                          >
                            {salaryTypeOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span
                            className={`badge ${
                              setup.salaryType === "EARNING"
                                ? "badge-success"
                                : "badge-danger"
                            }`}
                          >
                            {formatDisplayText(setup.salaryType)}
                          </span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            className="form-input"
                            value={setup.salaryName}
                            onChange={(e) =>
                              handleChange(index, "salaryName", e.target.value)
                            }
                          />
                        ) : (
                          <span>{setup.salaryName || "-"}</span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <select
                            className="form-select"
                            value={setup.contributionType || ""}
                            onChange={(e) =>
                              handleChange(
                                index,
                                "contributionType",
                                e.target.value,
                              )
                            }
                          >
                            {contributionTypeOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span>
                            {formatDisplayText(setup.contributionType)}
                          </span>
                        )}
                      </td>
                      <td>
                        <ToggleButton
                          isOn={setup.status === "ENABLED"}
                          onToggle={() => handleStatusToggle(index, setup)}
                          label={`Toggle status for ${setup.salaryName}`}
                          status={false}
                        />
                      </td>
                    </>
                  ) : (
                    <>
                      <td>
                        {isEditing ? (
                          <select
                            className="form-select"
                            value={setup.salaryType || ""}
                            onChange={(e) =>
                              handleChange(index, "salaryType", e.target.value)
                            }
                          >
                            {salaryTypeOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span
                            className={`badge ${
                              setup.salaryType === "EARNING"
                                ? "badge-success"
                                : "badge-danger"
                            }`}
                          >
                            {formatDisplayText(setup.salaryType)}
                          </span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            className="form-input"
                            value={setup.salaryName}
                            onChange={(e) =>
                              handleChange(index, "salaryName", e.target.value)
                            }
                          />
                        ) : (
                          <span>{setup.salaryName || "-"}</span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <select
                            className="form-select"
                            value={setup.contributionType || ""}
                            onChange={(e) =>
                              handleChange(
                                index,
                                "contributionType",
                                e.target.value,
                              )
                            }
                          >
                            {contributionTypeOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span>
                            {formatDisplayText(setup.contributionType)}
                          </span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="number"
                            className={`form-input ${
                              isValueFieldDisabled(setup.taxType)
                                ? "disabled"
                                : ""
                            }`}
                            value={setup.value}
                            onChange={(e) =>
                              handleChange(index, "value", e.target.value)
                            }
                            disabled={isValueFieldDisabled(setup.taxType)}
                          />
                        ) : (
                          <span>{setup.value || "-"}</span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <select
                            className="form-select"
                            value={setup.valueType || ""}
                            onChange={(e) =>
                              handleChange(index, "valueType", e.target.value)
                            }
                          >
                            <option value="PERCENTAGE">Percentage</option>
                            <option value="FIXED">Fixed</option>
                          </select>
                        ) : (
                          <span>{formatDisplayText(setup.valueType)}</span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="number"
                            className={`form-input ${
                              isValueFieldDisabled(setup.taxType)
                                ? "disabled"
                                : ""
                            }`}
                            value={setup.limitAmount}
                            onChange={(e) =>
                              handleChange(index, "limitAmount", e.target.value)
                            }
                            disabled={isValueFieldDisabled(setup.taxType)}
                          />
                        ) : (
                          <span>{setup.limitAmount || "-"}</span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <select
                            className="form-select"
                            value={setup.taxType || ""}
                            onChange={(e) =>
                              handleChange(index, "taxType", e.target.value)
                            }
                          >
                            <option value="IT">IT</option>
                            <option value="PT">PT</option>
                            <option value="NIL">NIL</option>
                            <option value="ESI">ESI</option>
                            <option value="PF">PF</option>
                          </select>
                        ) : (
                          <span className={`badge-tax ${setup.taxType}`}>
                            {setup.taxType || "-"}
                          </span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <select
                            className="form-select"
                            value={setup.paymentCalculationBasedOn || ""}
                            onChange={(e) =>
                              handleChange(
                                index,
                                "paymentCalculationBasedOn",
                                e.target.value,
                              )
                            }
                          >
                            <option value="GROSS_SALARY">Gross Salary</option>
                            <option value="PAYMENT_SETUP">Payment Setup</option>
                          </select>
                        ) : (
                          <span>
                            {formatDisplayText(setup.paymentCalculationBasedOn)}
                          </span>
                        )}
                      </td>
                      <td>
                        {isEditing &&
                        setup.paymentCalculationBasedOn !== "GROSS_SALARY" ? (
                          <div className="multi-select-list">
                            {[
                              ...new Set(
                                paymentSetups
                                  .filter((_, i) => i !== index)
                                  .map((item) => item.salaryName)
                                  .filter(Boolean),
                              ),
                            ].map((name) => (
                              <label key={name} className="multi-select-option">
                                <input
                                  type="checkbox"
                                  checked={(
                                    setup.paymentSetupBasedOnNames || []
                                  ).includes(name)}
                                  onChange={() =>
                                    handleToggleBasedOn(index, name)
                                  }
                                />
                                <span>{name}</span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <span
                            title={(setup.paymentSetupBasedOnNames || []).join(
                              ", ",
                            )}
                          >
                            {(setup.paymentSetupBasedOnNames || []).length
                              ? setup.paymentSetupBasedOnNames.join(", ")
                              : "-"}
                          </span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <ColoredCheckbox
                            color="green"
                            checked={setup.remainingEarnings || false}
                            name={`remainingEarnings_${index}`}
                            onChange={() =>
                              handleRemainingEarningsChange(index)
                            }
                            value={index}
                          />
                        ) : (
                          <span
                            className={`badge ${
                              setup.remainingEarnings
                                ? "badge-success"
                                : "badge-secondary"
                            }`}
                          >
                            {setup.remainingEarnings ? "Yes" : "No"}
                          </span>
                        )}
                      </td>
                      <td>
                        <ToggleButton
                          isOn={setup.status === "ENABLED"}
                          onToggle={() => handleStatusToggle(index, setup)}
                          label={`Toggle status for ${setup.salaryName}`}
                          status={false}
                        />
                      </td>
                    </>
                  )}
                  {isEditing && (
                    <td>
                      <IconButton
                        type="button"
                        onClick={() => handleRemove(index)}
                        icon="fluent:delete-28-regular"
                        variant="delete"
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <p>No payment setups available for this payroll.</p>
        </div>
      )}
    </div>
  );
}

export default PaymentSetupContent;
